import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { VapiProvider } from './contexts/VoiceCallContext';
import AuthPage from './components/Auth/AuthPage';
import SignupOptionsPage from './components/Auth/SignupOptionsPage';
import TrialCallPage from './components/Auth/TrialCallPage';
import RegistrationSuccessPage from './components/Auth/RegistrationSuccessPage';
import ForgotPasswordForm from './components/Auth/ForgotPasswordForm';
import ResetPasswordForm from './components/Auth/ResetPasswordForm';
import Dashboard from './components/Dashboard/Dashboard';
import DashboardHome from './components/Dashboard/DashboardHome';
import AssistantPage from './components/Assistant/AssistantPage';
import AdminPage from './components/Admin/AdminPage';
import UserProfilePage from './components/Admin/UserProfile/UserProfilePage';
import ClientDashboardPage from './components/ClientDashboard/ClientDashboardPage';
import ProfilePage from './components/Profile/ProfilePage';
import DeepLinkHandler from './components/DeepLinkHandler';
import { Toaster } from 'react-hot-toast';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/auth" replace />;
};

const DashboardRedirect = () => {
  const { userRole } = useAuth();
  if (userRole === 'COACH' || userRole === 'coach') {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

const ClientOnlyRoute = ({ children }) => {
  const { userRole, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (userRole === 'COACH' || userRole === 'coach') {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, authEvent, isPasswordRecovery, isProcessingTokens, inviteData, validateInvite, userRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isValidatingInvite, setIsValidatingInvite] = React.useState(false);
  const [inviteValidationComplete, setInviteValidationComplete] = React.useState(false);

  React.useEffect(() => {
    const validateRegistrationAccess = async () => {
      // Wait for token processing to complete
      if (isProcessingTokens) {
        return;
      }

      // Allow access to reset-password route during password recovery
      if (location.pathname === '/reset-password' && (isPasswordRecovery || authEvent === 'PASSWORD_RECOVERY')) {
        return;
      }
      
      // Handle registration route access with invite token validation
      if (location.pathname === '/register') {
        const urlParams = new URLSearchParams(location.search);
        const inviteToken = urlParams.get('invite');
        
        if (!inviteToken) {
          // No invite token provided - redirect to login
          console.warn('Registration access denied: No invite token provided');
          navigate('/auth', { replace: true });
          return;
        }
        
        // Validate the invite token if not already validated
        if (!inviteData && !isValidatingInvite && !inviteValidationComplete) {
          setIsValidatingInvite(true);
          try {
            const result = await validateInvite(inviteToken);
            if (!result.valid) {
              console.warn('Registration access denied: Invalid invite token');
              navigate('/auth', {
                replace: true,
                state: {
                  error: result.error || 'Invalid or expired invitation link'
                }
              });
              return;
            }
          } catch (error) {
            console.error('Registration access denied: Invite validation error', error);
            navigate('/auth', {
              replace: true,
              state: {
                error: 'Unable to validate invitation. Please try again or contact support.'
              }
            });
            return;
          } finally {
            setIsValidatingInvite(false);
            setInviteValidationComplete(true);
          }
        }
        
        // Token is valid or already validated - allow access
        return;
      }
      
      // Redirect authenticated users away from other public routes
      if (!loading && !isProcessingTokens && user && authEvent !== 'PASSWORD_RECOVERY' && !isPasswordRecovery) {
        if (userRole === 'COACH' || userRole === 'coach') {
          navigate('/admin');
        } else {
          navigate('/');
        }
      }
    };

    validateRegistrationAccess();
  }, [user, loading, authEvent, isPasswordRecovery, isProcessingTokens, location.pathname, location.search, navigate, inviteData, validateInvite, isValidatingInvite, inviteValidationComplete, userRole]);

  if (loading || isValidatingInvite || isProcessingTokens) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isValidatingInvite ? 'Validating invitation...' : isProcessingTokens ? 'Processing secure link...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <VapiProvider>
        <Router>
          <DeepLinkHandler />
          <div className="App">
            <Toaster
              position="top-right"
              containerStyle={{
                top: 'calc(1rem + env(safe-area-inset-top))',
                right: 'calc(1rem + env(safe-area-inset-right))',
                bottom: 'calc(1rem + env(safe-area-inset-bottom))',
                left: 'calc(1rem + env(safe-area-inset-left))',
              }}
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />
            
            <Routes>
              <Route
                path="/auth"
                element={
                  <PublicRoute>
                    <AuthPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignupOptionsPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup-options"
                element={
                  <PublicRoute>
                    <SignupOptionsPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/trial-call"
                element={<TrialCallPage />}
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <AuthPage initialMode="register" />
                  </PublicRoute>
                }
              />
              <Route
                path="/register/success"
                element={
                  <PublicRoute>
                    <RegistrationSuccessPage email={new URLSearchParams(window.location.search).get('email') || ''} />
                  </PublicRoute>
                }
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardRedirect />} />
                <Route
                  path="dashboard"
                  element={
                    <ClientOnlyRoute>
                      <DashboardHome />
                    </ClientOnlyRoute>
                  }
                />
                <Route path="assistants" element={<AssistantPage />} />
                <Route path="admin" element={<AdminPage />} />
                <Route path="admin/users/:userId" element={<UserProfilePage />} />
                <Route path="my-dashboard" element={<ClientDashboardPage />} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPasswordForm />
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <ResetPasswordForm />
                  </PublicRoute>
                }
              />
              {/* Catch-all route for password reset URLs with tokens */}
              <Route
                path="/access_token=/*"
                element={<Navigate to="/reset-password" replace />}
              />
              {/* Fallback route for any unmatched paths */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
                      <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                      <a
                        href="/auth"
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Go to Login
                      </a>
                    </div>
                  </div>
                }
              />
            </Routes>
          </div>
        </Router>
      </VapiProvider>
    </AuthProvider>
  );
}

export default App;